import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/** Lo que necesita la fila de cada pestaña de `dealsTabs` (home, categoría, tienda...). */
export interface PestanaOrden {
  tab: string;
  name: string;
  url?: string;
  hide?: boolean;
}

/** Velocímetro de la pestaña On Fire (también en el orden del panel de filtros). */
export const ICONO_ON_FIRE = 'M3.34 19a10 10 0 1 1 17.32 0M12 14l4-4';
const ICONOS: Record<string, string> = {
  // La popularidad es velocidad (km/h): nunca una llama (odd/tasks/avisos-movil.md, S11).
  onfire: ICONO_ON_FIRE,
  populares: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z',
  nuevos: 'M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6',
  comentados: 'M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z',
  para_ti: 'M12 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
};

const ORDEN = ['onfire', 'populares', 'nuevos', 'comentados'];
/** Las variantes de On Fire (onfire-amazon...) van primero; lo que no está en el diseño, al final. */
function puesto(tab: string) {
  const i = ORDEN.indexOf(tab.startsWith('onfire') ? 'onfire' : tab);
  return i < 0 ? ORDEN.length : i;
}

/**
 * Tercera fila de la cabecera de escritorio (diseño V26): las pestañas de orden con su
 * icono, «Filtrar» y el botón de tema. Es sólo presentación: quien la usa
 * (app-user-toolbar) decide qué hace cada pulsación con sus manejadores de siempre.
 * Spec: odd/tasks/tiendas-voz-cabecera.md, S5.
 */
@Component({
  selector: 'app-cabecera-orden',
  standalone: true,
  template: `
    <nav aria-label="Ordenar" class="pestanas">
      @for (p of visibles(); track p.tab) {
        <a class="tab" [class.on]="p.tab === actual()" [attr.href]="p.url || null"
           [attr.aria-current]="p.tab === actual() ? 'page' : null" (click)="pulsar($event, p)">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path [attr.d]="icono(p.tab)"/></svg>
          {{ p.name }}
        </a>
      }
    </nav>
    <span class="hueco"></span>
    @if (conFiltrar()) {
    <button type="button" class="btn" [attr.aria-label]="movil() ? 'Filtrar' : null" [attr.aria-expanded]="filtrosAbiertos()" (click)="filtrar.emit()">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12"/><circle cx="16" cy="6" r="2"/><circle cx="10" cy="12" r="2"/><circle cx="18" cy="18" r="2"/></svg>
      @if (!movil()) { Filtrar }
    </button>
    }
    <!-- En móvil el tema se cambia desde la pantalla Cuenta: aquí no cabe. -->
    @if (!movil()) {
    <button type="button" class="mi" [attr.aria-pressed]="!temaClaro()" (click)="tema.emit()">
      @if (temaClaro()) {
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M21 13A9 9 0 1 1 11 3a7 7 0 0 0 10 10z"/></svg>
      } @else {
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
      }
      <!-- Texto visible además del icono: accesibilidad para gente mayor (31/10/2026). -->
      <span class="mi__texto">{{ temaClaro() ? 'Modo oscuro' : 'Modo claro' }}</span>
    </button>
    }
  `,
  styles: [`
    :host { display: flex; align-items: center; gap: 22px; height: 48px; width: 100%; }
    .pestanas { display: flex; align-items: center; gap: 24px; height: 48px; }
    .tab {
      display: inline-flex; align-items: center; gap: 6px; height: 48px; padding: 0 4px;
      font: 600 14px/1 var(--mc-font-title); color: var(--c26-texto-3, #5A5560);
      white-space: nowrap; text-decoration: none; cursor: pointer;
    }
    .tab:hover { color: var(--c26-texto, #17151A); }
    .tab.on { color: #B3141A; box-shadow: inset 0 -2px 0 #C81A20; }
    .hueco { flex: 1; }
    .btn {
      display: inline-flex; align-items: center; gap: 8px; height: 44px; padding: 0 16px;
      border-radius: 999px; border: 1px solid var(--c26-borde, #DCD7D7); background: transparent;
      font: 700 13px/1 var(--mc-font-title); color: var(--c26-texto, #17151A); cursor: pointer;
    }
    .mi {
      height: 44px; padding: 0 16px; gap: 8px; border-radius: 999px; border: 1px solid var(--c26-borde, #DCD7D7); font: inherit; font-size: 14px; font-weight: 700; white-space: nowrap;
      background: transparent; color: var(--c26-texto, #17151A);
      display: inline-flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0;
    }
    .btn[aria-expanded='true'], .btn:hover, .mi:hover { border-color: #C81A20; }
    .tab:focus-visible, .btn:focus-visible, .mi:focus-visible { outline: 2px solid #C81A20; outline-offset: 2px; }
    :host-context(.theme-dark) {
      --c26-texto: #F4F2F2; --c26-texto-3: #C9C4CC; --c26-borde: #4A454F;
    }
    :host-context(.theme-dark) .tab.on { color: #FF6B70; }
    /* Móvil (odd/tasks/movil-cabecera-tarjeta-cuenta.md, S3): la misma fila, deslizable, con
       «Filtrar» como icono al final. */
    :host(.movil) { gap: 8px; height: 48px; }
    :host(.movil) .pestanas {
      flex: 1; min-width: 0; gap: 16px; overflow-x: auto; overflow-y: hidden;
      scrollbar-width: none; -webkit-overflow-scrolling: touch;
    }
    :host(.movil) .pestanas::-webkit-scrollbar { display: none; }
    :host(.movil) .tab { font-size: 14px; flex-shrink: 0; }
    :host(.movil) .hueco { display: none; }
    :host(.movil) .btn { width: 40px; height: 40px; padding: 0; justify-content: center; flex-shrink: 0; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.movil]': 'movil()' },
})
export class CabeceraOrdenComponent {
  readonly pestanas = input<PestanaOrden[]>([]);
  readonly actual = input<string>('onfire');
  readonly filtrosAbiertos = input<boolean>(false);
  /** Off on store pages: they have the filter panel (odd/tasks/filtros-categorias-tiendas.md). */
  readonly conFiltrar = input<boolean>(true);
  readonly temaClaro = input<boolean>(true);
  /** Versión de móvil: fila deslizable, «Filtrar» sólo icono y sin botón de tema. */
  readonly movil = input<boolean>(false);

  /** La pestaña pulsada, tal cual: quien la recibe ordena (y cambia la URL) como siempre. */
  readonly elegir = output<PestanaOrden>();
  readonly filtrar = output<void>();
  readonly tema = output<void>();

  /** Orden del diseño V26 (On Fire, Populares, Nuevos, Comentados), venga como venga de la página. */
  readonly visibles = computed(() => this.pestanas().filter((p) => !p.hide)
    .map((p, i) => ({ p, i }))
    .sort((a, b) => puesto(a.p.tab) - puesto(b.p.tab) || a.i - b.i)
    .map(({ p }) => p));

  /** Las variantes de On Fire (onfire-amazon...) llevan su velocímetro. */
  icono(tab: string) { return ICONOS[tab.startsWith('onfire') ? 'onfire' : tab] ?? ICONOS['populares']; }

  /** El enlace lleva href para buscadores y para abrir en otra pestaña; el clic normal lo gestiona la página. */
  pulsar(ev: MouseEvent, p: PestanaOrden) {
    if (ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.button === 1) return;
    ev.preventDefault();
    this.elegir.emit(p);
  }
}
